import {
  ScheduleSchema,
  hhmmToMinutes,
  type Schedule,
  type ScheduleTarget
} from '@kioskforge/shared';

/**
 * Pure schedule resolution (X2.1, per docs/SCHEDULE.md).
 *
 * `resolveNow` / `resolveWindow` are deterministic and side-effect-free, so the
 * server, the player and the admin preview return identical answers for
 * identical inputs. Timezones are handled with `Intl.DateTimeFormat`, so DST and
 * midnight rollover are correct without a date library (no new dependency).
 *
 * Precedence (highest wins): emergency push → (override vs grid block by
 * priority, override wins ties) → fallback. Blocks are half-open `[from, to)`
 * with `24:00` as the end-of-day sentinel.
 */

export type ResolutionSource = 'emergency' | 'override' | 'block' | 'fallback';

export interface Resolved {
  target: ScheduleTarget;
  source: ResolutionSource;
  blockId?: string;
  overrideDate?: string;
  /** ISO instants bounding this resolution. */
  startsAt: string;
  endsAt: string;
}

const MINUTE_MS = 60_000;
const DAY_MS = 86_400_000;

interface WallTime {
  date: string; // YYYY-MM-DD (local)
  minutes: number; // minutes since local midnight
  weekday: number; // 0 = Sunday … 6 = Saturday
}

/** Reads the local civil time of an instant in `timeZone`. */
function wallTime(at: Date, timeZone: string): WallTime {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
  const parts: Record<string, string> = {};
  for (const part of formatter.formatToParts(at)) parts[part.type] = part.value;
  const year = Number(parts.year);
  const month = Number(parts.month);
  const day = Number(parts.day);
  const hour = Number(parts.hour === '24' ? '00' : parts.hour);
  const minute = Number(parts.minute);
  const date = `${parts.year}-${parts.month}-${parts.day}`;
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return { date, minutes: hour * 60 + minute, weekday };
}

/** The UTC offset (ms) that `timeZone` observes at `utcMs`, DST-aware. */
function zoneOffset(timeZone: string, utcMs: number): number {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
  const parts: Record<string, string> = {};
  for (const part of formatter.formatToParts(new Date(utcMs))) parts[part.type] = part.value;
  const hour = Number(parts.hour === '24' ? '00' : parts.hour);
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    hour,
    Number(parts.minute),
    Number(parts.second)
  );
  return asUtc - Math.floor(utcMs / 1000) * 1000;
}

/** The instant for a local civil date/time. Refines once so DST gaps land on a real instant. */
function zonedInstant(timeZone: string, date: string, minutes: number): Date {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const guess = Date.UTC(y, m - 1, d) + minutes * MINUTE_MS;
  const first = guess - zoneOffset(timeZone, guess);
  const second = guess - zoneOffset(timeZone, first);
  return new Date(second);
}

function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const shifted = new Date(Date.UTC(y, m - 1, d + days));
  return shifted.toISOString().slice(0, 10);
}

function iso(timeZone: string, date: string, minutes: number): string {
  return zonedInstant(timeZone, date, minutes).toISOString();
}

interface Candidate {
  target: ScheduleTarget;
  source: 'override' | 'block';
  blockId?: string;
  overrideDate?: string;
  priority: number;
  /** Lower sorts first within a tier (later-defined wins → higher index). */
  index: number;
}

/**
 * Resolves what a screen shows at an instant. Validates first: an invalid
 * schedule throws rather than silently resolving wrong. Deterministic.
 */
export function resolveNow(schedule: Schedule, at: Date): Resolved {
  const parsed = ScheduleSchema.safeParse(schedule);
  if (!parsed.success) {
    throw new Error(`Invalid schedule: ${parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ')}`);
  }
  const doc = parsed.data;
  const zone = doc.timezone;
  const wall = wallTime(at, zone);
  const ms = at.getTime();

  // 1. Emergency push (bounded by its expiry).
  if (doc.emergency) {
    const start = Date.parse(doc.emergency.starts_at);
    const end = Date.parse(doc.emergency.expires_at);
    if (ms >= start && ms < end) {
      return {
        target: doc.emergency.target,
        source: 'emergency',
        startsAt: doc.emergency.starts_at,
        endsAt: doc.emergency.expires_at
      };
    }
  }

  // 2/3. Override (tier 2) vs grid blocks (tier 3), by priority; override wins ties.
  const candidates: Candidate[] = [];
  doc.overrides.forEach((override, index) => {
    if (override.date === wall.date) {
      candidates.push({ target: override.target, source: 'override', overrideDate: override.date, priority: override.priority, index });
    }
  });
  doc.blocks.forEach((block, index) => {
    if (!block.days.includes(wall.weekday)) return;
    const from = hhmmToMinutes(block.from);
    const to = hhmmToMinutes(block.to);
    if (wall.minutes >= from && wall.minutes < to) {
      candidates.push({ target: block.target, source: 'block', blockId: block.id, priority: block.priority, index });
    }
  });

  if (candidates.length) {
    const best = candidates.slice().sort((a, b) => {
      if (b.priority !== a.priority) return b.priority - a.priority;
      if (a.source !== b.source) return a.source === 'override' ? -1 : 1; // override beats block on a tie
      return b.index - a.index; // later-defined wins
    })[0] as Candidate;

    if (best.source === 'override') {
      return {
        target: best.target,
        source: 'override',
        overrideDate: best.overrideDate,
        startsAt: iso(zone, wall.date, 0),
        endsAt: iso(zone, addDays(wall.date, 1), 0)
      };
    }
    const block = doc.blocks[best.index] as (typeof doc.blocks)[number];
    return {
      target: best.target,
      source: 'block',
      blockId: best.blockId,
      startsAt: iso(zone, wall.date, hhmmToMinutes(block.from)),
      endsAt: iso(zone, wall.date, hhmmToMinutes(block.to))
    };
  }

  // 4. Fallback.
  return {
    target: doc.fallback,
    source: 'fallback',
    startsAt: iso(zone, wall.date, 0),
    endsAt: iso(zone, addDays(wall.date, 1), 0)
  };
}

function sameResolution(a: Resolved, b: Resolved): boolean {
  return (
    a.source === b.source &&
    a.blockId === b.blockId &&
    a.overrideDate === b.overrideDate &&
    JSON.stringify(a.target) === JSON.stringify(b.target)
  );
}

/**
 * Resolves a window into contiguous segments (for prefetch / the offline cache).
 * Minute-granular, merged where the resolution does not change.
 */
export function resolveWindow(schedule: Schedule, from: Date, to: Date): Resolved[] {
  if (to.getTime() <= from.getTime()) return [];
  const segments: Resolved[] = [];
  const start = Math.floor(from.getTime() / MINUTE_MS) * MINUTE_MS;
  for (let ms = start; ms < to.getTime(); ms += MINUTE_MS) {
    const current = resolveNow(schedule, new Date(ms));
    const last = segments[segments.length - 1];
    if (last && sameResolution(last, current)) {
      last.endsAt = current.endsAt;
    } else {
      segments.push({ ...current, startsAt: new Date(ms).toISOString() });
    }
  }
  return segments;
}
